import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { listCollections, listDeliveryBoys, deleteCollection, exportXlsxUrl, exportPivotXlsxUrl } from "@/lib/api";
import { formatINR } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Plus, Pencil, Trash2, Filter, Wallet, FileSpreadsheet, Grid3x3, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { waLink, buildDailyEntryMessage } from "@/lib/whatsapp";
import { useBranch } from "@/context/BranchContext";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const ALL = "all";

export default function Collections() {
  const [rows, setRows] = useState([]);
  const [boys, setBoys] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [boyId, setBoyId] = useState(ALL);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);
  const navigate = useNavigate();
  const { branchId } = useBranch();

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (boyId && boyId !== ALL) params.delivery_boy_id = boyId;
      const [c, b] = await Promise.all([listCollections(params), listDeliveryBoys()]);
      setRows(c);
      setBoys(b);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, boyId, branchId]);

  const totals = useMemo(() => {
    return rows.reduce(
      (a, r) => ({
        cash_collected: a.cash_collected + (r.cash_collected || 0),
        final_payable: a.final_payable + (r.final_payable || 0),
        paid_amount: a.paid_amount + (r.paid_amount || 0),
        balance: a.balance + (r.balance || 0),
      }),
      { cash_collected: 0, final_payable: 0, paid_amount: 0, balance: 0 }
    );
  }, [rows]);

  const clearFilters = () => {
    setStartDate("");
    setEndDate("");
    setBoyId(ALL);
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    try {
      await deleteCollection(deleteId);
      toast.success("Entry deleted");
      setDeleteId(null);
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const xlsxHref = exportXlsxUrl({
    start_date: startDate || undefined,
    end_date: endDate || undefined,
    delivery_boy_id: boyId !== ALL ? boyId : undefined,
  });
  const pivotHref = exportPivotXlsxUrl({
    start_date: startDate || undefined,
    end_date: endDate || undefined,
    group_by: "week",
  });

  return (
    <div className="space-y-6" data-testid="collections-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-zinc-500">Ledger</div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight mt-1">
            Collection Entries
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Every day&apos;s cash collection, deductions, payables and balances.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <a href={pivotHref} target="_blank" rel="noreferrer">
            <Button variant="outline" className="rounded-lg" data-testid="export-pivot-button">
              <Grid3x3 className="h-4 w-4 mr-1.5" /> Pivot Excel
            </Button>
          </a>
          <a href={xlsxHref} target="_blank" rel="noreferrer">
            <Button variant="outline" className="rounded-lg" data-testid="export-xlsx-button">
              <FileSpreadsheet className="h-4 w-4 mr-1.5" /> Excel
            </Button>
          </a>
          <Button
            onClick={() => navigate("/collections/new")}
            className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-lg"
            data-testid="new-collection-btn"
          >
            <Plus className="h-4 w-4 mr-1.5" /> New Entry
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-zinc-200 p-4 flex flex-wrap items-end gap-3">
        <div className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-zinc-500 pr-1">
          <Filter className="h-3.5 w-3.5" /> Filter
        </div>
        <div className="space-y-1">
          <label className="text-xs text-zinc-500">From</label>
          <Input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="w-40"
            data-testid="filter-start-date"
          />
        </div>
        <div className="space-y-1">
          <label className="text-xs text-zinc-500">To</label>
          <Input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="w-40"
            data-testid="filter-end-date"
          />
        </div>
        <div className="space-y-1 min-w-[200px]">
          <label className="text-xs text-zinc-500">Delivery Boy</label>
          <Select value={boyId} onValueChange={setBoyId}>
            <SelectTrigger data-testid="filter-delivery-boy">
              <SelectValue placeholder="All delivery boys" />
            </SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value={ALL}>All delivery boys</SelectItem>
              {boys.map((b) => (
                <SelectItem key={b.id} value={b.id}>
                  {b.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {(startDate || endDate || boyId !== ALL) && (
          <Button variant="ghost" onClick={clearFilters} data-testid="clear-filters-btn">
            Clear
          </Button>
        )}
        <div className="ml-auto flex items-center gap-6 text-right tabular pl-4">
          <StatInline label="Cash" value={totals.cash_collected} />
          <StatInline label="Payable" value={totals.final_payable} />
          <StatInline label="Paid" value={totals.paid_amount} tone="positive" />
          <StatInline label="Balance" value={totals.balance} tone="warning" />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-zinc-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr className="text-left">
              <Th>Date</Th>
              <Th>Delivery Boy</Th>
              <Th className="text-right">Cash Collected</Th>
              <Th className="text-right">Earning</Th>
              <Th className="text-right">Shop Ded.</Th>
              <Th className="text-right">Other Ded.</Th>
              <Th className="text-right">Payable</Th>
              <Th className="text-right">Paid</Th>
              <Th className="text-right">Balance</Th>
              <Th>Method</Th>
              <Th className="text-right">Actions</Th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={11} className="text-center py-8 text-zinc-500">Loading…</td>
              </tr>
            )}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={11} className="text-center py-14">
                  <div className="inline-flex flex-col items-center gap-2 text-zinc-500">
                    <Wallet className="h-8 w-8 text-zinc-300" />
                    <div className="font-medium text-zinc-700">No entries yet</div>
                    <div className="text-xs">Create your first collection entry to get started.</div>
                    <Link to="/collections/new">
                      <Button className="mt-3 bg-emerald-700 hover:bg-emerald-800 text-white">
                        <Plus className="h-4 w-4 mr-1.5" /> New Entry
                      </Button>
                    </Link>
                  </div>
                </td>
              </tr>
            )}
            {rows.map((r) => (
              <tr
                key={r.id}
                className="border-b border-zinc-100 hover:bg-zinc-50 tabular"
                data-testid={`collection-row-${r.id}`}
              >
                <td className="px-4 py-3 whitespace-nowrap">{r.entry_date}</td>
                <td className="px-4 py-3 font-medium text-zinc-900">
                  {r.delivery_boy_name || "—"}
                  {r.min_guarantee_applied > 0 && (
                    <Badge className="ml-2 bg-rose-50 text-rose-600 border border-rose-200 hover:bg-rose-50">
                      MG +{formatINR(r.min_guarantee_applied)}
                    </Badge>
                  )}
                </td>
                <td className="px-4 py-3 text-right">{formatINR(r.cash_collected)}</td>
                <td className="px-4 py-3 text-right">{formatINR(r.earning)}</td>
                <td className="px-4 py-3 text-right">{formatINR(r.shop_deduction)}</td>
                <td className="px-4 py-3 text-right">{formatINR(r.other_deduction)}</td>
                <td className="px-4 py-3 text-right font-semibold">{formatINR(r.final_payable)}</td>
                <td className="px-4 py-3 text-right text-emerald-700">{formatINR(r.paid_amount)}</td>
                <td className={`px-4 py-3 text-right font-semibold ${r.balance > 0 ? "text-amber-700" : "text-emerald-700"}`}>
                  {formatINR(r.balance)}
                </td>
                <td className="px-4 py-3">
                  <PaymentBadges pbm={r.paid_by_method} legacyMethod={r.payment_method} legacyPaid={r.paid_amount} />
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <a
                    href={waLink(
                      (boys.find((b) => b.id === r.delivery_boy_id) || {}).phone,
                      buildDailyEntryMessage(r)
                    )}
                    target="_blank"
                    rel="noreferrer"
                    title="Send daily statement on WhatsApp"
                  >
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-emerald-700"
                      data-testid={`collection-wa-${r.id}`}
                    >
                      <MessageCircle className="h-4 w-4" />
                    </Button>
                  </a>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => navigate(`/collections/${r.id}`)}
                    data-testid={`collection-edit-${r.id}`}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setDeleteId(r.id)}
                    className="text-rose-600 hover:text-rose-700"
                    data-testid={`collection-delete-${r.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this collection entry?</AlertDialogTitle>
            <AlertDialogDescription>This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-rose-600 hover:bg-rose-700 text-white"
              onClick={confirmDelete}
              data-testid="collection-delete-confirm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

const Th = ({ children, className = "" }) => (
  <th className={`px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-zinc-500 ${className}`}>
    {children}
  </th>
);

const StatInline = ({ label, value, tone = "default" }) => {
  const cls = { default: "text-zinc-900", positive: "text-emerald-700", warning: "text-amber-700" }[tone];
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`font-display text-sm font-semibold ${cls}`}>{formatINR(value)}</div>
    </div>
  );
};

const PaymentBadges = ({ pbm, legacyMethod, legacyPaid }) => {
  // Fallback to legacy single-method data when paid_by_method missing/empty
  const src = pbm || {};
  const hasAny = ["cash", "upi", "bank", "other"].some((k) => (src[k] || 0) > 0);
  const parts = hasAny
    ? [
        ["Cash", src.cash, "bg-emerald-50 text-emerald-700 border-emerald-200"],
        ["UPI", src.upi, "bg-blue-50 text-blue-700 border-blue-200"],
        ["Bank", src.bank, "bg-violet-50 text-violet-700 border-violet-200"],
        ["Other", src.other, "bg-zinc-50 text-zinc-700 border-zinc-200"],
      ].filter(([, v]) => (v || 0) > 0)
    : legacyPaid > 0
      ? [[
          (legacyMethod || "cash").toUpperCase(),
          legacyPaid,
          "bg-zinc-50 text-zinc-700 border-zinc-200",
        ]]
      : [];
  if (parts.length === 0) {
    return <span className="text-xs text-zinc-400">—</span>;
  }
  return (
    <div className="flex flex-wrap gap-1">
      {parts.map(([label, v, cls]) => (
        <span
          key={label}
          className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[11px] font-medium tabular ${cls}`}
        >
          {label} {formatINR(v)}
        </span>
      ))}
    </div>
  );
};
